import * as React from "react";
import { cn } from "@/lib/utils";

export interface StarRatingProps {
  /** Current rating value (0 to max) */
  value: number;
  /** Callback when rating changes. If omitted, component is non-interactive. */
  onChange?: (value: number) => void;
  /** Number of stars to display (default 2) */
  max?: number;
  /** Color of active (filled) stars */
  activeColor?: string;
  /** Color of inactive (empty) stars. Ignored when readOnly is true. */
  inactiveColor?: string;
  /** CSS font-size for the stars */
  size?: string;
  /** CSS gap between stars */
  gap?: string;
  /** Enable glow text-shadow on active stars */
  glow?: boolean;
  /** Glow shadow color (requires glow=true) */
  glowColor?: string;
  /** Bold font weight on stars */
  bold?: boolean;
  /**
   * Read-only display mode: renders only the filled stars inline,
   * hiding empty ones. Useful for metadata previews.
   */
  readOnly?: boolean;
  /** data-testid for the wrapper */
  "data-testid"?: string;
  /** data-testid prefix for individual stars (produces `${prefix}-${starIndex}`) */
  starTestIdPrefix?: string;
  className?: string;
  style?: React.CSSProperties;
}

export function StarRating({
  value,
  onChange,
  max = 2,
  activeColor = "#ffaa00",
  inactiveColor = "#333",
  size,
  gap = "2px",
  glow = false,
  glowColor,
  bold = false,
  readOnly = false,
  "data-testid": testId,
  starTestIdPrefix,
  className,
  style,
}: StarRatingProps) {
  // Read-only display: just render filled stars inline
  if (readOnly) {
    if (value <= 0) return null;
    return (
      <div
        data-testid={testId}
        className={cn("flex", className)}
        style={{ gap, alignItems: "center", ...style }}
      >
        {Array.from({ length: Math.min(value, max) }, (_, i) => i + 1).map((star) => (
          <i
            key={star}
            className="bx bxs-star"
            style={{
              color: activeColor,
              fontSize: size,
              lineHeight: 1,
            }}
          />
        ))}
      </div>
    );
  }

  // Interactive / display mode with all stars visible
  return (
    <div
      data-testid={testId}
      className={cn("flex", className)}
      style={{ gap, alignItems: "center", ...style }}
    >
      {Array.from({ length: max }, (_, i) => i + 1).map((star) => {
        const isActive = value >= star;
        return (
          <i
            key={star}
            className={isActive ? "bx bxs-star" : "bx bx-star"}
            data-testid={starTestIdPrefix ? `${starTestIdPrefix}-${star}` : undefined}
            onClick={
              onChange
                ? (e) => {
                    e.stopPropagation();
                    onChange(value === star ? 0 : star);
                  }
                : undefined
            }
            style={{
              cursor: onChange ? "pointer" : "default",
              color: isActive ? activeColor : inactiveColor,
              fontSize: size,
              lineHeight: 1,
              transition: glow ? "all 0.2s" : undefined,
              textShadow: glow && isActive && glowColor ? `0 0 5px ${glowColor}` : "none",
            }}
          />
        );
      })}
    </div>
  );
}
